import { api, get, post, put, del, download } from './client.js';
import { auth } from '../auth/firebase.js';

/**
 * Super Admin API client（/admin 區塊用）
 *   - overview：經銷商 → tenant 樹（backend saasAdmin /overview，super_admin gate）
 *   - me：登入身分（含 accountRole）
 *   - executeProposal：ActionProposalCard 確認後執行 http 類提案
 */
export const superAdminApi = {
    /** 版本更新清單 xlsx（PM 每週要的「M/D–M/D 版本更新紀錄」）— from/to：'YYYY-MM-DD'，含頭尾 */
    downloadReleaseNotes: (from, to) => download(`/api/ivr/saas-admin/release-notes.xlsx?${new URLSearchParams({ from, to })}`),

    me: async () => {
        const idToken = await auth.currentUser?.getIdToken();
        const r = await fetch('/me', { headers: { Authorization: `Bearer ${idToken}` } });
        if (!r.ok) throw new Error(`/me ${r.status}`);
        return r.json();
    },
    overview: () => get('/api/ivr/saas-admin/overview'),
    // ── 加值分鐘數（灌入分鐘數頁）──
    minutePacks: () => get('/api/ivr/saas-admin/minute-packs'),
    loadMinutes: (body) => post('/api/ivr/saas-admin/minute-packs', body),
    patchMinutePack: (orderNo, body) => api('PATCH', `/api/ivr/saas-admin/minute-packs/${encodeURIComponent(orderNo)}`, { body }),
    settleMinutePayment: (body) => post('/api/ivr/saas-admin/minute-payments', body),
    emailMinuteStatement: (body) => post('/api/ivr/saas-admin/minute-packs/statement-email', body),
    setBillingMode: (uid, body) => api('PATCH', `/api/ivr/saas-admin/parents/${encodeURIComponent(uid)}`, { body }),
    statsOverview: (month) => get(`/api/ivr/saas-admin/stats-overview${month ? `?month=${month}` : ''}`),
    billingOverview: (month, role = 'business') => get(`/api/ivr/saas-admin/billing/overview?role=${role}${month ? `&month=${month}` : ''}`),
    deleteParent: (uid) => del(`/api/ivr/saas-admin/parents/${encodeURIComponent(uid)}`),
    /** 一次到位開戶（帳號+席次+tenants+membership）— 商用不帶 createdBy 時後端預設歸 osense */
    createParent: (payload) => post('/api/ivr/saas-admin/parents', payload),
    tenant: (hostId) => get(`/api/ivr/admin/tenants/${encodeURIComponent(hostId)}`),
    registrations: (hostId) => get(`/api/ivr/admin/tenants/${encodeURIComponent(hostId)}/registrations`),
    tenantAvatars: (hostId) => get(`/api/ivr/admin/tenant-avatars?hostId=${encodeURIComponent(hostId)}`),
    seats: (uid) => get(`/api/ivr/saas-admin/parents/${encodeURIComponent(uid)}/seats`),
    /**
     * 調整既有帳號的席次 — super_admin only（後端 requireSuperAdmin）。
     * effectiveDate：'YYYY-MM-DD'（UTC+8），補登過去的擴線日用；不帶 = 今天生效。
     */
    updateSeats: (uid, seats, effectiveDate) => api('PATCH', `/api/ivr/saas-admin/parents/${encodeURIComponent(uid)}`, {
        body: { seats, ...(effectiveDate ? { seatsEffectiveDate: effectiveDate } : {}) },
    }),

    /**
     * 開通 / 收回某租戶的「SIP Trunk 自助切換」— super_admin only。
     * 開通後該租戶的商用帳號才能在自己的 PBX 設定頁切 extension ↔ trunk（仍要過相容性清單）。
     */
    /**
     * 商用帳號的 API 開關（per tenant）— super_admin only。
     * patch = { apiDocsEnabled?: boolean, avatarApiEnabled?: boolean }；角色管理要 API 文件開著才能開（後端 409）。
     */
    setApiAccess: async (hostId, patch) => {
        const idToken = await auth.currentUser?.getIdToken();
        const r = await fetch(`/api/ivr/saas-admin/tenants/${encodeURIComponent(hostId)}/api-access`, {
            method: 'PATCH',
            headers: { Authorization: `Bearer ${idToken}`, 'Content-Type': 'application/json' },
            body: JSON.stringify(patch),
        });
        const data = await r.json().catch(() => ({}));
        if (!r.ok) throw new Error(data.message || data.error || `${r.status}`);
        return data;
    },

    setTrunkSelfService: async (hostId, enabled) => {
        const idToken = await auth.currentUser?.getIdToken();
        const r = await fetch(`/api/ivr/saas-admin/tenants/${encodeURIComponent(hostId)}/trunk-self-service`, {
            method: 'PATCH',
            headers: { Authorization: `Bearer ${idToken}`, 'Content-Type': 'application/json' },
            body: JSON.stringify({ enabled: !!enabled }),
        });
        const data = await r.json().catch(() => ({}));
        if (!r.ok) throw new Error(data.message || data.error || `${r.status}`);
        return data;
    },

    /** 支援工單（dealer 診斷判系統端 → 開到超管佇列）— super_admin only */
    tickets: (status = 'open') => get(`/api/ivr/saas-admin/tickets?status=${encodeURIComponent(status)}`),
    resolveTicket: async (ticketId) => {
        const idToken = await auth.currentUser?.getIdToken();
        const r = await fetch(`/api/ivr/saas-admin/tickets/${encodeURIComponent(ticketId)}`, {
            method: 'PATCH',
            headers: { Authorization: `Bearer ${idToken}`, 'Content-Type': 'application/json' },
            body: JSON.stringify({}),
        });
        const data = await r.json().catch(() => ({}));
        if (!r.ok) throw new Error(data.error || `${r.status}`);
        return data;
    },

    /** 清空超管對話 → 下一句話開全新 claude session（skills 重新載入） */
    resetChat: async (hostId) => {
        const idToken = await auth.currentUser?.getIdToken();
        const r = await fetch(`/ivr-builder/api/admin/tenants/${encodeURIComponent(hostId)}/chat/reset`, {
            method: 'POST',
            headers: { Authorization: `Bearer ${idToken}` },
        });
        const data = await r.json().catch(() => ({}));
        if (!r.ok) throw new Error(data.detail || data.error || `${r.status}`);
        return data;
    },

    /** 執行 action-proposal（kind=http）— method/endpoint/params 來自卡片 */
    executeProposal: async (proposal) => {
        const { method = 'PUT', endpoint, params } = proposal || {};
        if (!endpoint || !endpoint.startsWith('/api/')) throw new Error('提案缺少合法 endpoint');
        const m = String(method).toUpperCase();
        if (m === 'GET') return get(endpoint);
        if (m === 'PUT') return put(endpoint, params || {});
        // POST / PATCH / DELETE 走原生 fetch（client.js 只包了部分動詞）
        const idToken = await auth.currentUser?.getIdToken();
        const r = await fetch(endpoint, {
            method: m,
            headers: {
                Authorization: `Bearer ${idToken}`,
                'Content-Type': 'application/json',
            },
            body: m === 'DELETE' ? undefined : JSON.stringify(params || {}),
        });
        const data = await r.json().catch(() => ({}));
        if (!r.ok) throw new Error(data.message || data.error || `${r.status}`);
        return data;
    },
};
